import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { notificationsApi, type ApiNotification } from '@/api/notifications';

export type NotificationType =
  | 'proposal_received'
  | 'proposal_approved'
  | 'proposal_rejected'
  | 'edit_requested'
  | 'appointment_upcoming'
  | 'appointment_confirmed'
  | 'appointment_reminder'
  | 'document_shared'
  | 'access_granted'
  | 'access_revoked'
  | 'vaccine_due'
  | 'reminder'
  | 'reward_earned'
  | 'social'
  | 'pet_parks'
  | 'record_added'
  | 'system';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  petId?: string;
  petName?: string;
  actionRequired?: boolean;
  actionLink?: string;
  icon?: string;
  professionalName?: string;
  recordType?: string;
  proposalId?: string;
  source?: string;
}

interface NotificationsContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  clearAll: () => Promise<void>;
  getNotificationsForPet: (petId: string) => Notification[];
  getActionRequiredNotifications: () => Notification[];
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

function toNotification(n: ApiNotification): Notification {
  return {
    id: n.id,
    type: n.type as NotificationType,
    title: n.title,
    message: n.message,
    timestamp: n.createdAt,
    read: n.read,
    petId: n.petId,
    petName: n.petName,
    actionRequired: n.actionRequired,
    actionLink: n.actionLink,
    icon: n.icon,
    professionalName: n.professionalName,
    recordType: n.recordType,
    proposalId: n.proposalId,
  };
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const { items, unreadCount: uc } = await notificationsApi.list({ limit: 100 });
      setNotifications(items.map(toNotification));
      setUnreadCount(uc);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll every 30s
  useEffect(() => {
    const interval = setInterval(() => { refresh(); }, 30000);
    return () => clearInterval(interval);
  }, [refresh]);

  const addNotification = useCallback(async (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    try {
      const created = await notificationsApi.create(notification as any);
      setNotifications(prev => [toNotification(created), ...prev]);
      setUnreadCount(prev => prev + 1);
    } catch (err) {
      console.error('Failed to create notification:', err);
    }
  }, []);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationsApi.markRead(notificationId);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
    }
  }, []);

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      const notif = notifications.find(n => n.id === notificationId);
      await notificationsApi.remove(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (notif && !notif.read) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  }, [notifications]);

  const clearAll = useCallback(async () => {
    try {
      await notificationsApi.clearAll();
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to clear notifications:', err);
    }
  }, []);

  const getNotificationsForPet = (petId: string) => {
    return notifications.filter(n => n.petId === petId);
  };

  const getActionRequiredNotifications = () => {
    return notifications.filter(n => n.actionRequired && !n.read);
  };

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        refresh,
        addNotification,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAll,
        getNotificationsForPet,
        getActionRequiredNotifications,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
}
